import { startOfDay, startOfWeek, addDays, toDateKey, toWeekKey } from "@/lib/dateUtils";

/** Prompts per week required to maintain a weekly streak. */
export const WEEKLY_GOAL = 3;

/** Count consecutive days with activity, ending at today and walking backwards. */
export function getCurrentDailyStreak(dayKeys: Set<string>) {
  let streak = 0;
  let cursor = startOfDay(new Date());

  while (dayKeys.has(toDateKey(cursor))) {
    streak += 1;
    cursor = addDays(cursor, -1);
  }

  return streak;
}

/** Longest run of consecutive calendar days with activity. */
export function getLongestDailyStreak(dayKeys: Set<string>) {
  const days = Array.from(dayKeys)
    .map((key) => new Date(`${key}T00:00:00`))
    .sort((a, b) => a.getTime() - b.getTime());

  let longest = 0;
  let current = 0;
  let previous: Date | null = null;

  days.forEach((day) => {
    if (!previous) {
      current = 1;
    } else {
      const diffDays = Math.round((day.getTime() - previous.getTime()) / 86400000);
      current = diffDays === 1 ? current + 1 : 1;
    }

    longest = Math.max(longest, current);
    previous = day;
  });

  return longest;
}

/** Count consecutive weeks meeting WEEKLY_GOAL, ending at current/previous week. */
export function getCurrentWeeklyStreak(weekCounts: Map<string, number>) {
  let streak = 0;
  let cursor = startOfWeek(new Date());

  if ((weekCounts.get(toWeekKey(cursor)) || 0) < WEEKLY_GOAL) {
    cursor = addDays(cursor, -7);
  }

  while ((weekCounts.get(toWeekKey(cursor)) || 0) >= WEEKLY_GOAL) {
    streak += 1;
    cursor = addDays(cursor, -7);
  }

  return streak;
}

/** Longest run of consecutive weeks meeting WEEKLY_GOAL. */
export function getLongestWeeklyStreak(weekCounts: Map<string, number>) {
  const qualifiedWeeks = Array.from(weekCounts.entries())
    .filter(([, count]) => count >= WEEKLY_GOAL)
    .map(([key]) => new Date(`${key}T00:00:00`))
    .sort((a, b) => a.getTime() - b.getTime());

  let longest = 0;
  let current = 0;
  let previous: Date | null = null;

  qualifiedWeeks.forEach((week) => {
    if (!previous) {
      current = 1;
    } else {
      const diffWeeks = Math.round((week.getTime() - previous.getTime()) / (7 * 86400000));
      current = diffWeeks === 1 ? current + 1 : 1;
    }

    longest = Math.max(longest, current);
    previous = week;
  });

  return longest;
}

/**
 * Build day-key set and week-count map from timestamped items.
 * Reusable across any page that needs streak data.
 */
export function buildStreakMaps(timestamps: Date[]) {
  const dayKeys = new Set<string>();
  const weekCounts = new Map<string, number>();

  timestamps.forEach((ts) => {
    dayKeys.add(toDateKey(ts));
    const weekKey = toWeekKey(ts);
    weekCounts.set(weekKey, (weekCounts.get(weekKey) || 0) + 1);
  });

  return { dayKeys, weekCounts };
}
